import { supabase } from './supabaseClient';

/**
 * Launch asset types that can be generated
 */
export type LaunchAssetType = 
  | 'social_media_posts'
  | 'email_campaign'
  | 'press_release'
  | 'product_hunt_launch'
  | 'launch_checklist'
  | 'promotional_graphics_text'
  | 'testimonial_requests'
  | 'landing_page_copy';

/**
 * Launch asset category for organization
 */
export type LaunchAssetCategory = 
  | 'marketing'
  | 'communications'
  | 'operations'
  | 'content';

/**
 * Launch asset option with description
 */
export interface LaunchAssetOption {
  id: LaunchAssetType;
  title: string;
  description: string;
  category: LaunchAssetCategory;
  icon: string;
  estimatedTime: string;
  aiPowered: boolean;
}

/**
 * Generated launch asset
 */
export interface GeneratedLaunchAsset {
  type: LaunchAssetType;
  title: string;
  content: string | any;
  metadata?: {
    platform?: string;
    format?: string;
    wordCount?: number;
    characterCount?: number;
  };
}

/**
 * Context for launch asset generation
 */
export interface LaunchAssetContext {
  projectTitle: string;
  projectDescription: string;
  keyFeatures: string[];
  targetAudience?: string;
  proposedPrice?: number;
  earlyBirdDiscount?: number;
  projectCategory?: string;
  demoUrl?: string;
  preReleaseParticipants?: number;
  market?: string;
  persona?: string;
}

/**
 * AI Launch Assets Generator Service
 * Generates various launch campaign assets using AI
 */
export class AILaunchAssetsService {
  // Use Supabase Edge Function for secure AI API calls
  private static readonly AI_PROVIDER = 'deepseek';
  private static readonly EDGE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-openrouter-proxy`;
  private static readonly timeout = 30000;

  /**
   * Get all available launch asset options
   * @param t - Optional translation function for i18n support
   */
  static getAssetOptions(t?: (key: string) => string): LaunchAssetOption[] {
    // Helper function to get translation or fallback to English
    const translate = (key: string, fallback: string): string => {
      return t ? t(key) : fallback;
    };

    return [
      {
        id: 'social_media_posts',
        title: translate('launchAssets.assetTypes.socialMediaPosts.title', 'Social Media Announcement Posts'),
        description: translate('launchAssets.assetTypes.socialMediaPosts.description', 'Generate ready-to-post content for Twitter, LinkedIn, Facebook, and Instagram'),
        category: 'marketing',
        icon: 'bi-share-fill',
        estimatedTime: translate('launchAssets.assetTypes.socialMediaPosts.time', '30 seconds'),
        aiPowered: true
      },
      {
        id: 'email_campaign',
        title: translate('launchAssets.assetTypes.emailCampaign.title', 'Email Launch Campaign'),
        description: translate('launchAssets.assetTypes.emailCampaign.description', 'Create personalized email templates for pre-release testers and early supporters'),
        category: 'communications',
        icon: 'bi-envelope-fill',
        estimatedTime: translate('launchAssets.assetTypes.emailCampaign.time', '1 minute'),
        aiPowered: true
      },
      {
        id: 'press_release',
        title: translate('launchAssets.assetTypes.pressRelease.title', 'Press Release'),
        description: translate('launchAssets.assetTypes.pressRelease.description', 'Professional press release for media outreach and publication'),
        category: 'communications',
        icon: 'bi-newspaper',
        estimatedTime: translate('launchAssets.assetTypes.pressRelease.time', '1 minute'),
        aiPowered: true
      },
      {
        id: 'product_hunt_launch',
        title: translate('launchAssets.assetTypes.productHuntLaunch.title', 'Product Hunt Launch Kit'),
        description: translate('launchAssets.assetTypes.productHuntLaunch.description', 'Tagline, description, and first comment for Product Hunt launch'),
        category: 'marketing',
        icon: 'bi-rocket-takeoff-fill',
        estimatedTime: translate('launchAssets.assetTypes.productHuntLaunch.time', '45 seconds'),
        aiPowered: true
      },
      {
        id: 'launch_checklist',
        title: translate('launchAssets.assetTypes.launchChecklist.title', 'Launch Day Checklist'),
        description: translate('launchAssets.assetTypes.launchChecklist.description', 'Comprehensive checklist of all tasks for launch day'),
        category: 'operations',
        icon: 'bi-list-check',
        estimatedTime: translate('launchAssets.assetTypes.launchChecklist.time', '30 seconds'),
        aiPowered: true
      },
      {
        id: 'promotional_graphics_text',
        title: translate('launchAssets.assetTypes.promotionalGraphicsText.title', 'Promotional Copy for Graphics'),
        description: translate('launchAssets.assetTypes.promotionalGraphicsText.description', 'Compelling text for promotional graphics and banners'),
        category: 'content',
        icon: 'bi-palette-fill',
        estimatedTime: translate('launchAssets.assetTypes.promotionalGraphicsText.time', '30 seconds'),
        aiPowered: true
      },
      {
        id: 'testimonial_requests',
        title: translate('launchAssets.assetTypes.testimonialRequests.title', 'Testimonial Request Messages'),
        description: translate('launchAssets.assetTypes.testimonialRequests.description', 'Templates to request reviews and testimonials from beta testers'),
        category: 'communications',
        icon: 'bi-chat-quote-fill',
        estimatedTime: translate('launchAssets.assetTypes.testimonialRequests.time', '30 seconds'),
        aiPowered: true
      },
      {
        id: 'landing_page_copy',
        title: translate('launchAssets.assetTypes.landingPageCopy.title', 'Landing Page Copy'),
        description: translate('launchAssets.assetTypes.landingPageCopy.description', 'Conversion-optimized copy for your launch landing page'),
        category: 'content',
        icon: 'bi-file-earmark-text-fill',
        estimatedTime: translate('launchAssets.assetTypes.landingPageCopy.time', '1 minute'),
        aiPowered: true
      }
    ];
  }

  /**
   * Get language name from language code
   */
  private static getLanguageName(languageCode: string): string {
    const languageMap: Record<string, string> = {
      'en': 'English',
      'zh': 'Chinese (中文)',
      'es': 'Spanish (Español)',
      'fr': 'French (Français)',
      'de': 'German (Deutsch)',
      'ja': 'Japanese (日本語)',
      'ko': 'Korean (한국어)',
      'pt': 'Portuguese (Português)',
      'ru': 'Russian (Русский)',
      'ar': 'Arabic (العربية)',
      'hi': 'Hindi (हिन्दी)',
      'it': 'Italian (Italiano)'
    };
    return languageMap[languageCode] || 'English';
  }

  /**
   * Generate specific launch asset using AI
   * @param assetType - Type of asset to generate
   * @param context - Project context
   * @param language - Language code for content generation
   * @param projectId - Optional project ID for metadata storage
   * @param t - Optional translation function for i18n support
   */
  static async generateAsset(
    assetType: LaunchAssetType,
    context: LaunchAssetContext,
    language: string = 'en',
    projectId?: string,
    t?: (key: string) => string
  ): Promise<GeneratedLaunchAsset> {
    const prompt = this.createPromptForAssetType(assetType, context, language);
    
    let aiResponse: string;
    try {
      aiResponse = await this.callAI(prompt);
    } catch (error) {
      console.warn(`AI generation failed for ${assetType}, using fallback content:`, error);
      aiResponse = this.getFallbackContent(assetType, context);
    }
    
    // Store language metadata in localStorage for the asset
    if (projectId) {
      const assetId = `${projectId}_${assetType}`;
      try {
        localStorage.setItem(`content_language_assets_${assetId}`, language);
        console.log(`✅ Stored language metadata for asset: ${assetId} (${language})`);
      } catch (error) {
        console.warn(`⚠️ Failed to store language metadata for asset ${assetId}:`, error);
      }
    }
    
    return {
      type: assetType,
      title: this.getAssetTitle(assetType, t),
      content: this.parseAIResponse(assetType, aiResponse),
      metadata: this.getMetadata(assetType, aiResponse)
    };
  }

  /**
   * Generate multiple assets at once
   * @param assetTypes - Array of asset types to generate
   * @param context - Project context
   * @param language - Language code for content generation
   * @param projectId - Optional project ID for metadata storage
   * @param t - Optional translation function for i18n support
   */
  static async generateMultipleAssets(
    assetTypes: LaunchAssetType[],
    context: LaunchAssetContext,
    language: string = 'en',
    projectId?: string,
    t?: (key: string) => string
  ): Promise<GeneratedLaunchAsset[]> {
    const promises = assetTypes.map(type => this.generateAsset(type, context, language, projectId, t));
    return Promise.all(promises);
  }

  /**
   * Create AI prompt based on asset type
   */
  private static createPromptForAssetType(
    type: LaunchAssetType,
    context: LaunchAssetContext,
    language: string = 'en'
  ): string {
    const baseContext = this.formatContext(context);
    
    const languageInstruction = language !== 'en' 
      ? `\n\nIMPORTANT: Generate ALL content in ${this.getLanguageName(language)} language. All text, captions, headlines, and copy must be in ${this.getLanguageName(language)}.`
      : '';

    const prompts: Record<LaunchAssetType, string> = {
      social_media_posts: `Generate 4 engaging social media announcement posts for launching "${context.projectTitle}".${languageInstruction}

${baseContext}

Create posts for:
1. Twitter/X (280 characters max, include 2-3 relevant hashtags)
2. LinkedIn (professional tone, 1300 characters max)
3. Facebook (engaging, conversational, 400 words max)
4. Instagram caption (visual-focused, 2200 characters max, include 10-15 hashtags)

Each post should:
- Highlight key benefits
- Include a clear call-to-action
- Generate excitement
- Be platform-appropriate

Format as JSON array with platform, text, and hashtags.`,

      email_campaign: `Create a launch email campaign for "${context.projectTitle}" to send to pre-release testers and early supporters.${languageInstruction}

${baseContext}

Generate 3 email templates:
1. "Early Access Announcement" - For pre-release testers (personal, grateful tone)
2. "Official Launch" - For general subscribers (exciting, professional)
3. "Launch Day Special" - For interested users (urgency, special offer)

Each email should include:
- Compelling subject line
- Personal greeting placeholder
- Body copy with benefits
- Clear CTA button text
- P.S. line for extra engagement

Format as JSON array with subject, body, cta, and ps fields.`,

      press_release: `Write a professional press release for the launch of "${context.projectTitle}".${languageInstruction}

${baseContext}

Structure:
- Headline (attention-grabbing)
- Dateline (City, Date)
- Lead paragraph (who, what, when, where, why)
- 2-3 body paragraphs (details, features, benefits)
- Quote from founder (inspirational, visionary)
- Company boilerplate
- Contact information placeholder

Keep it newsworthy, factual, and under 500 words.
Format as structured text with clear sections.`,

      product_hunt_launch: `Create a complete Product Hunt launch kit for "${context.projectTitle}".${languageInstruction}

${baseContext}

Generate:
1. Tagline (10 words max, catchy and memorable)
2. Product description (260 characters max, SEO-friendly)
3. First comment (detailed explanation, 500 words, markdown formatted)
4. Maker introduction (warm, personal, 200 words)
5. 5 discussion starter questions for the community

Format as JSON with tagline, description, firstComment, makerIntro, and questions fields.`,

      launch_checklist: `Create a comprehensive launch day checklist for "${context.projectTitle}".${languageInstruction}

${baseContext}

Organize tasks by time:
- Pre-Launch (1 week before)
- Launch Day Morning
- Launch Day Afternoon/Evening
- Post-Launch (first 3 days)

Include tasks for:
- Technical preparation
- Marketing channels (social media, Product Hunt, Reddit, HN)
- Community engagement
- Monitoring and analytics
- Customer support readiness

Format as JSON array with category, time, task, and priority (high/medium/low).`,

      promotional_graphics_text: `Generate compelling copy for promotional graphics for "${context.projectTitle}".${languageInstruction}

${baseContext}

Create text for:
1. Hero banner (main headline + subheadline)
2. Feature highlights (5 short benefit statements, 10 words each)
3. Social media graphics (3 variations of key message, 15 words each)
4. Call-to-action buttons (5 variations)
5. Testimonial graphics prompts (3 quote placeholders with context)

Keep punchy, benefit-focused, and visually friendly (short lines).
Format as JSON with each type as a key.`,

      testimonial_requests: `Write testimonial and review request messages for "${context.projectTitle}" pre-release testers.${languageInstruction}

${baseContext}

Create 4 message templates:
1. Personal email to top testers (warm, appreciative)
2. In-app notification (brief, actionable)
3. Social media DM (casual, friendly)
4. Survey invitation with incentive (professional, value-focused)

Each should:
- Thank them for participation
- Explain the importance of reviews
- Make it easy (include direct links)
- Offer something in return (if applicable)

Format as JSON array with channel, subject, message, and cta fields.`,

      landing_page_copy: `Write conversion-optimized landing page copy for "${context.projectTitle}" launch.${languageInstruction}

${baseContext}

Create complete copy for:
1. Hero section (headline, subheadline, CTA)
2. Problem statement (pain points your audience faces)
3. Solution (how your product solves it)
4. Key features (3-4 features with icons and descriptions)
5. Benefits (3 benefit statements)
6. Social proof section (placeholder text)
7. Pricing section copy
8. FAQ (5 common questions and answers)
9. Final CTA section

Use persuasive copywriting techniques, focus on benefits over features.
Format as JSON with each section as a key.`
    };

    return prompts[type];
  }

  /**
   * Format context for AI prompt
   */
  private static formatContext(context: LaunchAssetContext): string {
    let formatted = `Project: ${context.projectTitle}\n`;
    formatted += `Description: ${context.projectDescription}\n`;
    
    if (context.keyFeatures.length > 0) {
      formatted += `\nKey Features:\n${context.keyFeatures.map(f => `- ${f}`).join('\n')}\n`;
    }
    
    if (context.targetAudience) {
      formatted += `\nTarget Audience: ${context.targetAudience}\n`;
    }
    
    if (context.proposedPrice) {
      formatted += `\nPrice: $${context.proposedPrice}`;
      if (context.earlyBirdDiscount) {
        const discountedPrice = context.proposedPrice * (1 - context.earlyBirdDiscount / 100);
        formatted += ` (Early Bird: $${discountedPrice.toFixed(2)} - ${context.earlyBirdDiscount}% off)`;
      }
      formatted += `\n`;
    }
    
    if (context.projectCategory) {
      formatted += `\nCategory: ${context.projectCategory}\n`;
    }
    
    if (context.demoUrl) {
      formatted += `\nDemo: ${context.demoUrl}\n`;
    }
    
    if (context.preReleaseParticipants) {
      formatted += `\nPre-release testers: ${context.preReleaseParticipants} users\n`;
    }
    
    return formatted;
  }

  /**
   * Call AI API via Supabase Edge Function
   */
  private static async callAI(prompt: string): Promise<string> {
    const systemMessage = 'You are an expert marketing copywriter and launch strategist. Generate professional, compelling launch assets that drive conversions and engagement. Always return responses in the requested format (JSON or structured text).';
    
    const fullPrompt = `${systemMessage}\n\n${prompt}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session. Please sign in.');
      }

      const requestBody = {
        provider: this.AI_PROVIDER,
        prompt: fullPrompt,
        operation: 'generate-launch-assets',
        temperature: 0.8,
        maxTokens: 2000
      };

      const response = await fetch(this.EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Edge Function error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error('Invalid response from Edge Function');
      }

      return data.data;
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('AI API call failed:', error);
      
      // Re-throw so the caller can handle fallback
      throw error;
    }
  }

  /**
   * Parse AI response based on asset type
   */
  private static parseAIResponse(type: LaunchAssetType, response: string): any {
    try {
      // Try to parse as JSON first
      const jsonMatch = response.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (e) {
      // If JSON parsing fails, return as text
    }
    
    return response;
  }

  /**
   * Get metadata for generated asset
   */
  private static getMetadata(type: LaunchAssetType, content: string): any {
    return {
      wordCount: content.split(/\s+/).length,
      characterCount: content.length,
      generatedAt: new Date().toISOString()
    };
  }

  /**
   * Get asset title
   * @param type - Asset type
   * @param t - Optional translation function for i18n support
   */
  private static getAssetTitle(type: LaunchAssetType, t?: (key: string) => string): string {
    const option = this.getAssetOptions(t).find(opt => opt.id === type);
    return option?.title || type;
  }

  /**
   * Get fallback content when AI fails
   */
  private static getFallbackContent(assetType: LaunchAssetType, context: LaunchAssetContext): string {
    const fallbacks: Record<LaunchAssetType, () => string> = {
      social_media_posts: () => JSON.stringify({
        twitter: `🚀 Exciting news! We're launching ${context.projectTitle} - ${context.projectDescription.substring(0, 100)}... Check it out: ${context.demoUrl || 'Coming soon!'}\n\n#startup #launch #${context.projectCategory || 'tech'}`,
        linkedin: `I'm thrilled to announce the launch of ${context.projectTitle}!\n\n${context.projectDescription}\n\nKey features:\n${context.keyFeatures.map(f => `• ${f}`).join('\n')}\n\n${context.demoUrl ? `Try it: ${context.demoUrl}` : ''}\n\n#entrepreneurship #startup #launch`,
        facebook: `🎉 Big launch day! ${context.projectTitle} is now live!\n\n${context.projectDescription}\n\nWhat makes it special:\n${context.keyFeatures.slice(0, 3).map(f => `✅ ${f}`).join('\n')}\n\n${context.demoUrl ? `👉 ${context.demoUrl}` : 'Sign up for early access!'}`,
        instagram: `🚀 LAUNCH DAY! ${context.projectTitle} is here! \n\n${context.projectDescription.substring(0, 150)}...\n\n✨ Key features:\n${context.keyFeatures.slice(0, 3).map(f => `• ${f}`).join('\n')}\n\nLink in bio! #startup #launch #${context.projectCategory || 'innovation'}`
      }),
      email_campaign: () => JSON.stringify([{
        subject: `🚀 ${context.projectTitle} is Launching!`,
        preview: `Be among the first to try ${context.projectTitle}`,
        greeting: "Hi there,",
        body: `I'm excited to share that ${context.projectTitle} is now launching!\n\n${context.projectDescription}\n\nKey features include:\n${context.keyFeatures.map(f => `• ${f}`).join('\n')}\n\n${context.proposedPrice ? `Pricing starts at $${context.proposedPrice}${context.earlyBirdDiscount ? ` (Early bird discount: ${context.earlyBirdDiscount}% off!)` : ''}` : ''}\n\nReady to get started? ${context.demoUrl || 'Sign up now!'}\n\nBest regards,\nThe ${context.projectTitle} Team`,
        cta: "Try it Now",
        ctaUrl: context.demoUrl || "#"
      }]),
      press_release: () => `FOR IMMEDIATE RELEASE\n\n${context.projectTitle} Launches Innovative Solution\n\n[City, Date] - ${context.projectTitle} today announced the launch of its innovative platform designed to ${context.projectDescription.substring(0, 100)}.\n\n${context.projectDescription}\n\nKey features include:\n${context.keyFeatures.map(f => `• ${f}`).join('\n')}\n\n"${context.projectDescription.split('.')[0]}." said the founder.\n\n${context.proposedPrice ? `Pricing starts at $${context.proposedPrice}.` : ''}\n\nFor more information, visit: ${context.demoUrl || 'Coming soon'}\n\nAbout ${context.projectTitle}\n${context.projectTitle} is ${context.projectDescription.substring(0, 200)}...\n\nMedia Contact:\n[Your Name]\n[Your Email]\n[Your Phone]\n\n###\n\n`,
      product_hunt_launch: () => JSON.stringify({
        tagline: `${context.projectTitle} - ${context.projectDescription.substring(0, 60)}...`,
        description: `${context.projectDescription}\n\nKey Features:\n${context.keyFeatures.map(f => `• ${f}`).join('\n')}\n\n${context.demoUrl ? `Try it: ${context.demoUrl}` : ''}`,
        first_comment: `🚀 Exciting to launch ${context.projectTitle} on Product Hunt!\n\nWe've been working hard to build something that ${context.projectDescription.substring(0, 100)}...\n\nWhat makes ${context.projectTitle} special:\n${context.keyFeatures.slice(0, 3).map(f => `✨ ${f}`).join('\n')}\n\nWould love to hear your thoughts! ${context.demoUrl ? `Try it here: ${context.demoUrl}` : ''}\n\n#${context.projectCategory || 'tech'} #startup #launch`
      }),
      launch_checklist: () => JSON.stringify({
        pre_launch: [
          "✅ Final testing completed",
          "✅ Demo video ready",
          "✅ Website live and optimized",
          "✅ Social media accounts set up",
          "✅ Email list built",
          "✅ Press kit prepared"
        ],
        launch_day: [
          "📢 Announce on social media",
          "📧 Send launch email to subscribers",
          "🔗 Share on relevant communities/forums",
          "📱 Post on Product Hunt/Indie Hackers",
          "💬 Engage with early users",
          "📊 Monitor analytics and feedback"
        ],
        post_launch: [
          "📈 Track user signups and engagement",
          "💬 Respond to feedback and questions",
          "🔄 Iterate based on user input",
          "📢 Share success metrics",
          "🌟 Collect testimonials",
          "📅 Plan next milestones"
        ]
      }),
      promotional_graphics_text: () => JSON.stringify([
        `🚀 ${context.projectTitle} is LIVE! 🚀\n${context.projectDescription.substring(0, 80)}...\n\n✨ ${context.keyFeatures[0] || 'Amazing features await!'}\n\n${context.demoUrl ? `👉 ${context.demoUrl}` : 'Sign up now!'}`,
        `🎯 Solve your problems with ${context.projectTitle}\n\n❌ Problem: [Your target problem]\n✅ Solution: ${context.projectDescription.substring(0, 100)}...\n\n${context.keyFeatures.slice(0, 2).map(f => `⭐ ${f}`).join('\n')}\n\nReady to transform? ${context.demoUrl || 'Join the waitlist!'}`,
        `💡 Why choose ${context.projectTitle}?\n\n${context.keyFeatures.slice(0, 3).map((f, i) => `${i + 1}. ${f}`).join('\n')}\n\n${context.proposedPrice ? `💰 Starting at $${context.proposedPrice}` : '🚀 Launch pricing available!'}\n\n${context.demoUrl ? `🔗 ${context.demoUrl}` : 'Learn more today!'}`
      ]),
      testimonial_requests: () => JSON.stringify([
        {
          channel: "email",
          subject: "Help us share your experience with ${context.projectTitle}",
          message: `Hi [User Name],\n\nThank you for being an early tester of ${context.projectTitle}!\n\nYour feedback has been invaluable, and we'd love to feature your experience. Would you be open to sharing a quick testimonial?\n\nEven a few sentences about what you liked or how it helped you would mean the world to us.\n\nYou can share here: [Testimonial form link]\n\nThank you!\nThe ${context.projectTitle} Team`
        },
        {
          channel: "in-app",
          subject: "Share your thoughts!",
          message: `"How has ${context.projectTitle} helped you?"\n\nYour feedback helps us improve and helps other users discover great tools like yours!\n\n[5-star rating]\n[Text box for testimonial]`
        }
      ]),
      landing_page_copy: () => JSON.stringify({
        hero_headline: `Introducing ${context.projectTitle}`,
        hero_subheadline: context.projectDescription,
        hero_cta: "Get Started Free",
        problem: `Are you tired of ${context.targetAudience ? `being a ${context.targetAudience} struggling with` : 'facing challenges that'} ${context.projectDescription.split('.')[0].toLowerCase()}?`,
        solution: `${context.projectTitle} solves this by providing ${context.keyFeatures[0] || 'innovative solutions'}.`,
        features: context.keyFeatures.slice(0, 3).map(f => ({
          title: f.split(':')[0] || f.substring(0, 30),
          description: f.split(':')[1] || f,
          icon: "✨"
        })),
        benefits: [
          `Save time with ${context.projectTitle}`,
          `Improve your workflow`,
          `Join thousands of satisfied users`
        ],
        pricing: context.proposedPrice ? `Starting at $${context.proposedPrice}/month` : "Free to get started",
        faq: [
          {
            question: "How does it work?",
            answer: `${context.projectTitle} ${context.projectDescription.substring(0, 100).toLowerCase()}...`
          },
          {
            question: "Is there a free trial?",
            answer: "Yes! Sign up today and try it free."
          }
        ],
        final_cta: "Start Your Free Trial Today"
      })
    };

    return fallbacks[assetType] ? fallbacks[assetType]() : JSON.stringify({
      error: true,
      message: 'Unable to generate content. Please try again later.',
      fallback: true
    });
  }

  /**
   * Fetch pre-release project context from database
   */
  static async getProjectContext(projectId: string): Promise<LaunchAssetContext | null> {
    try {
      const { data: project, error } = await supabase
        .from('pre_release_projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error || !project) {
        return null;
      }

      // Get pre-release participants count
      const { data: interests } = await supabase
        .from('project_interest')
        .select('id')
        .eq('project_id', projectId);

      return {
        projectTitle: project.title,
        projectDescription: project.description,
        keyFeatures: Array.isArray(project.key_features) 
          ? project.key_features.map((f: any) => typeof f === 'string' ? f : f.text)
          : [],
        targetAudience: project.concept_details?.match(/\*\*Target Audience:\*\* ([^\n]+)/)?.[1],
        proposedPrice: project.proposed_price,
        earlyBirdDiscount: project.early_bird_discount,
        projectCategory: project.project_category,
        demoUrl: project.demo_url,
        preReleaseParticipants: interests?.length || 0
      };
    } catch (error) {
      console.error('Error fetching project context:', error);
      return null;
    }
  }

  /**
   * Fetch journey context for additional insights
   */
  static async getJourneyContext(journeyId: string): Promise<{ market?: string; persona?: string } | null> {
    try {
      const { data: journey, error } = await supabase
        .from('journeys')
        .select('market, persona')
        .eq('id', journeyId)
        .single();

      if (error || !journey) {
        return null;
      }

      return {
        market: journey.market,
        persona: journey.persona
      };
    } catch (error) {
      console.error('Error fetching journey context:', error);
      return null;
    }
  }

  /**
   * Get the user's most recent pre-release project
   */
  static async getLatestPreReleaseProject(userId: string): Promise<string | null> {
    try {
      console.log('🔍 Searching for latest project for user:', userId);
      
      // Get current authenticated user to ensure we're querying with proper auth
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('❌ User not authenticated:', authError);
        return null;
      }
      
      if (user.id !== userId) {
        console.error('❌ User ID mismatch:', { requested: userId, authenticated: user.id });
        return null;
      }
      
      console.log('✅ User authenticated, proceeding with query');
      
      const { data: project, error } = await supabase
        .from('pre_release_projects')
        .select('id, publisher_id')
        .eq('publisher_id', userId)
        .eq('is_active', true)  // Only get active projects
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('🔍 Query result:', { 
        project: project ? { id: project.id, publisher_id: project.publisher_id } : null, 
        error: error ? { code: error.code, message: error.message } : null 
      });

      if (error) {
        if (error.code === 'PGRST116') {
          console.log('⚠️ No project found for user (this is normal if no projects exist)');
          return null;
        }
        console.error('❌ Error fetching latest project:', error);
        return null;
      }
      
      if (!project) {
        console.log('⚠️ No project found for user:', userId);
        return null;
      }

      console.log('✅ Found project:', project.id);
      return project.id;
    } catch (error) {
      console.error('❌ Exception fetching latest project:', error);
      return null;
    }
  }
}
